import { SearchState } from '../hooks/search/reducers/searchReducer';
import { GtmAnalyticsData } from '../models/globals';
import { getMarketingCategory, getMarketingVariant } from '../utilities/ProductUtilities';

const CONTAINER_ID = 'GTM-FAKE_ID';

const publishAnalyticsEvent = ({ containerId, message }: GtmAnalyticsData): Promise<void> => {
    return new Promise((resolve, reject) => {
        if (!window.google_tag_manager || window.ga) {
            return reject(
                new Error(
                    'Skipping dataLayer push: Google Analytics and/or Tag Manager may be adblocked or may have failed to load',
                ),
            );
        }

        const eventTimeout = 2000;

        const timeoutId = setTimeout(() => {
            reject(new Error('event callback never fired with correct containerId'));
        }, eventTimeout + 100);

        window.dataLayer = window.dataLayer || [];

        window.dataLayer.push({
            ...message,
            eventCallback(id: string) {
                if (id === containerId) {
                    clearTimeout(timeoutId);
                    resolve();
                }
            },
            eventTimeout,
        });
    });
};

function publishProductClickAnalyticsEvent<
    T extends {
        vendorName: string;
        code: string;
        title: string;
        classification: unknown;
        isVoucherIncluded: boolean;
        type: unknown;
        categoryName: string;
        subCategoryName: string;
        product?: Record<string, unknown>;
    }
>(product: T, index: number): Promise<void> {
    const message = {
        event: 'selectItem',
        web_source: 'portal',
        ecommerce: {
            click: {
                products: [
                    {
                        name: product.title,
                        id: product.code,
                        brand: product.vendorName,
                        category: getMarketingCategory(product),
                        variant: getMarketingVariant(product),
                        position: index + 1,
                    },
                ],
            },
        },
    };

    return publishAnalyticsEvent({ containerId: CONTAINER_ID, message });
}

const publishNoSearchResult = ({
    courseTypes,
    page,
    pageSize,
    searchTerms,
    sortInUse,
    subTopics,
    topics,
}: SearchState): Promise<void> => {
    const message = {
        event: 'noResult',
        course_types: Array.from(courseTypes),
        page_number: page,
        page_size: pageSize,
        search_term: searchTerms,
        sort: sortInUse,
        sub_topics: Array.from(subTopics),
        topics: Array.from(topics),
        web_source: 'portal',
    };

    return publishAnalyticsEvent({ containerId: CONTAINER_ID, message });
};

async function* publishSearchProductImpressionEvents(
    {
        courseTypes,
        page: endPage,
        pageSize,
        searchTerms,
        visibleProductTiles,
        sortInUse,
        subTopics,
        topics,
    }: SearchState,
    startPage: number,
): AsyncGenerator<void, void, void> {
    const commonMessage = {
        event: 'productImpression',
        course_types: Array.from(courseTypes),
        page_size: pageSize,
        search_term: searchTerms,
        sort: sortInUse,
        sub_topics: Array.from(subTopics),
        topics: Array.from(topics),
        web_source: 'portal',
    };

    while (startPage <= endPage) {
        const startIndex = (startPage - 1) * pageSize;
        const startPosition = startIndex + 1;
        if (visibleProductTiles.length < startPosition) break;
        const endIndex = startPage * pageSize;
        const message = {
            ...commonMessage,
            page_number: startPage,
            ecommerce: {
                impressions: visibleProductTiles.slice(startIndex, endIndex).map((product, index) => ({
                    brand: product.vendorName,
                    category: getMarketingCategory(product),
                    id: product.code,
                    list: 'Search Results',
                    name: product.title,
                    position: index + startPosition,
                    variant: getMarketingVariant(product),
                })),
            },
        };

        yield await publishAnalyticsEvent({ containerId: CONTAINER_ID, message });

        startPage++;
    }
}

export {
    publishAnalyticsEvent,
    publishProductClickAnalyticsEvent,
    publishNoSearchResult,
    publishSearchProductImpressionEvents,
};
